import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getProxiedImageUrl, getProductPath, matchesProductSearch, scoreProductSearch } from '../utils/helpers';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SearchBar = ({ className = '' }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewResults, setPreviewResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    // Load all products on mount
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, allProducts]);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      setAllProducts(response.data);
    } catch (error) {
    }
  };

  const updatePreview = () => {
    if (!searchTerm || searchTerm.length < 2) {
      setPreviewResults([]);
      setShowPreview(false);
      return;
    }

    const results = allProducts
      .filter((p) => matchesProductSearch(p, searchTerm))
      // Rank: name matches first, then brand/sku, category, description, compat
      .sort((a, b) => scoreProductSearch(b, searchTerm) - scoreProductSearch(a, searchTerm))
      .slice(0, 5);

    setPreviewResults(results);
    setShowPreview(results.length > 0);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setShowPreview(false);
      // Navigate to products page with search query
      navigate(`/products?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleProductClick = (product) => {
    setShowPreview(false);
    setSearchTerm('');
    navigate(getProductPath(product));
  };

  return (
    <div className={`relative w-full ${className}`}>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyPress={handleSearchKeyPress}
        onFocus={() => searchTerm.length >= 2 && setShowPreview(true)}
        onBlur={() => setTimeout(() => setShowPreview(false), 200)}
        placeholder={language === 'it' ? '🔍 Cerca prodotti...' : language === 'es' ? '🔍 Buscar productos...' : '🔍 Search products...'}
        className="w-full bg-white text-black border border-gray-300 rounded px-3 py-3 pl-10 focus:outline-none focus:border-primary transition-colors text-sm sm:text-base placeholder-gray-600 font-medium"
      />
      <svg 
        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" 
        width="20" 
        height="20" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.35-4.35"/>
      </svg>
      {searchTerm && (
        <button
          onClick={() => {
            setSearchTerm('');
            setShowPreview(false);
          }}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xl"
        >
          ✕
        </button>
      )}
      
      {/* Preview Dropdown */}
      {showPreview && previewResults.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl max-h-[400px] overflow-y-auto z-50">
          {previewResults.map((product) => (
            <div
              key={product.sku}
              onClick={() => handleProductClick(product)}
              className="flex items-center gap-4 p-3 hover:bg-white/5 cursor-pointer transition-colors border-b border-white/5 last:border-0"
            >
              <div className="w-16 h-16 flex-shrink-0 bg-white/5 rounded overflow-hidden">
                {product.images && product.images[0] ? (
                  <img 
                    src={getProxiedImageUrl(product.images[0])}
                    alt={language === 'it' ? product.name_it : product.name_en}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = document.createElement('div');
                      fallback.className = 'w-full h-full flex items-center justify-center text-white/20 text-xs';
                      fallback.textContent = 'No Image';
                      e.target.parentElement.appendChild(fallback);
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">
                    No Image
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">
                  {language === 'it' ? product.name_it : product.name_en}
                </div>
                <div className="text-sm text-white/60">
                  SKU: {product.sku}
                  {product.brand && ` • ${product.brand}`}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-lg font-bold" style={{ color: 'var(--brand-primary)' }}>
                  €{product.price.toFixed(2)}
                </div>
                {product.stock > 0 ? (
                  <div className="text-xs text-green-500">
                    {language === 'it' ? 'Disponibile' : 'Available'}
                  </div>
                ) : (
                  <div className="text-xs text-red-500">
                    {language === 'it' ? 'Esaurito' : 'Out of Stock'}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="p-3 text-center text-sm text-white/60 bg-white/5">
            {language === 'it' 
              ? `Premi Invio per vedere tutti i risultati` 
              : `Press Enter to see all results`}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
